import { useFormikContext } from "formik";
import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { whitelistingStatusColor } from "../../../common/constants/common.constants";
import {
  AreaOfInterest,
  DomainOfExpertise,
  WhitelistingStatus,
} from "../../../common/enums/whitelisting.enums";
import { UserContext } from "../../../contexts/userContext";
import WizardField from "../../WizardField/WizardField";
import "./WhitelistingRequest.scss";

export interface IWhitelistingProps {
  monkeInterests: {
    label: string;
    name: string;
    initialValue: string;
  };
  personalBackground: {
    label: string;
    name: string;
    initialValue: string;
  };
  areasOfInterest: {
    label: string;
    name: string;
    initialValue: string;
  };
  domainOfExpertise: {
    label: string;
    name: string;
    initialValue: string;
  };
  disabled: boolean;
}

const WhitelistingRequest: FC<IWhitelistingProps> = ({
  monkeInterests,
  personalBackground,
  areasOfInterest,
  domainOfExpertise,
  disabled,
}) => {
  const { setFieldValue } = useFormikContext<IWhitelistingProps>();
  const { user, isWhitelisted } = useContext(UserContext);
  const [areAllDisabled, setAreAllDisabled] = useState(false);

  useEffect(() => {
    if (user?.whitelisting) {
      setFieldValue(monkeInterests.name, user.whitelisting.interests);

      setFieldValue(
        personalBackground.name,
        user.whitelisting.personalBackground
      );

      setFieldValue(
        areasOfInterest.name,
        user.whitelisting.areasOfInterest.map((area) => {
          return {
            label: area,
            value: area.toLocaleLowerCase(),
          };
        })
      );

      setFieldValue(
        domainOfExpertise.name,
        user.whitelisting.domainOfExpertise.map((domain) => {
          return {
            label: domain,
            value: domain.toLocaleLowerCase(),
          };
        })
      );

      setAreAllDisabled(
        isWhitelisted || user.whitelisting.status !== WhitelistingStatus.Denied
      );
    } else {
      setAreAllDisabled(isWhitelisted);
    }
  }, [user, isWhitelisted]);

  const getUserAreasOfInterests = useMemo(() => {
    if (user?.whitelisting) {
      return user.whitelisting.areasOfInterest.map((area) => {
        return {
          label: area,
          value: area.toLocaleLowerCase(),
        };
      });
    } else {
      return [];
    }
  }, [user]);

  const getUserDomainsOfExpertise = useMemo(() => {
    if (user?.whitelisting) {
      return user.whitelisting.domainOfExpertise.map((domain) => {
        return {
          label: domain,
          value: domain.toLocaleLowerCase(),
        };
      });
    } else {
      return [];
    }
  }, [user]);

  return (
    <div className="whitelisting-request">
      <div className="whitelisting-request__title-status">
        <p className="user-account-settings__wallet-label">
          Whitelisting request
        </p>
      </div>
      <WizardField
        name={monkeInterests.name}
        label={monkeInterests.label}
        placeholder="Unique.vc Syndicate interests"
        type="textarea"
        disabled={areAllDisabled}
      />
      <WizardField
        name={personalBackground.name}
        label={personalBackground.label}
        placeholder="Personal background"
        type="textarea"
        disabled={areAllDisabled}
      />
      <WizardField
        type="creatable-select"
        name={areasOfInterest.name}
        placeholder="Areas of interest"
        label={areasOfInterest.label}
        defaultCreatableSelect={getUserAreasOfInterests}
        selectOptions={Object.keys(AreaOfInterest).map((area) => {
          return {
            label: area,
            value: AreaOfInterest[area],
          };
        })}
        onChange={(e) => {
          setFieldValue(areasOfInterest.name, e);
        }}
        disabled={areAllDisabled}
      />
      <WizardField
        type="creatable-select"
        name={domainOfExpertise.name}
        placeholder="Domain of expertise"
        label={domainOfExpertise.label}
        selectOptions={Object.keys(DomainOfExpertise).map((area) => {
          return {
            label: area,
            value: DomainOfExpertise[area],
          };
        })}
        defaultCreatableSelect={getUserDomainsOfExpertise}
        onChange={(e) => {
          setFieldValue(domainOfExpertise.name, e);
        }}
        disabled={areAllDisabled}
      />
    </div>
  );
};

export default WhitelistingRequest;
